import { CSSProperties } from 'react';
import LoginIcon from '@mui/icons-material/Login';
import { useGuest } from 'hooks/useGuest';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { saloonButtonStyle, saloonHoverScaleIn, saloonHoverScaleOut } from 'styles/saloonButtons';

// Guest CTAs carry long "Login or Sign up…" labels. Pin the button to 44px —
// the same height as the chat / hand-history icon buttons it sits beside in the
// action bar (TopAction mobileIconBtn / iconBtnBaseStyle) — so the row aligns.
// border-box + zero vertical padding keeps the height exactly 44px; the label
// and horizontal padding scale to the action SLOT width (cqi — the slot is a
// query container, see TopAction portraitActionsStyle) so the long copy shrinks
// to fit between the history/chat icons instead of overflowing. Members keep the
// fixed saloon treatment.
const guestCtaButtonStyle: CSSProperties = {
  ...saloonButtonStyle,
  height: '44px',
  minHeight: '44px',
  boxSizing: 'border-box',
  padding: '0 clamp(0.4rem, 2cqi, 2.4rem)',
  fontSize: 'clamp(0.62rem, 4cqi, 1rem)'
};

type SaloonJoinCtaProps = {
  /** Button text — already resolved per guest/member by the caller. */
  label: string;
  /** Stable aria-label — tests and a11y key off this. */
  ariaLabel: string;
  onClick: () => void;
};

/**
 * The shared saloon "join" CTA rendered in the table action bar — every Join
 * Table / Join Private Table / Rejoin / guest "Login or Sign up to join" variant
 * in JoinButton renders through this. Centralizing it keeps the guest treatment
 * (44px height, cqi scaling, login icon) and the member treatment from drifting
 * apart across branches. The guest vs member style is chosen here from
 * `useGuest()`; the caller only supplies the resolved label, aria-label, and
 * click handler.
 */
const SaloonJoinCta = ({ label, ariaLabel, onClick }: SaloonJoinCtaProps) => {
  const guest = useGuest();
  const supportsHover = useSupportsHover();
  // Scale-only hover, attached only where a real hover pointer exists so touch
  // devices keep their first-tap click (see useSupportsHover).
  const hoverProps = supportsHover
    ? { onMouseEnter: saloonHoverScaleIn, onMouseLeave: saloonHoverScaleOut }
    : {};

  return (
    <button
      type='button'
      aria-label={ariaLabel}
      style={guest ? guestCtaButtonStyle : saloonButtonStyle}
      onClick={onClick}
      {...hoverProps}>
      <LoginIcon style={{ fontSize: '1.1em' }} />
      {label}
    </button>
  );
};

export default SaloonJoinCta;
